import { useState, useEffect, useCallback } from 'react';

function getLocalStorageValue<T>(storageKey: string, defaultValue: T): T {
  const item = localStorage.getItem(storageKey);
  return item ? (JSON.parse(item) as T) : defaultValue;
}

function setLocalStorageValue<T>(storageKey: string, value: T) {
  localStorage.setItem(storageKey, JSON.stringify(value));
}

export const useLocalStorage = <T,>(key: string, initialValue: T): [T, (value: T) => void] => {
  const [storedValue, setStoredValue] = useState(() => getLocalStorageValue(key, initialValue));

  useEffect(() => {
    setLocalStorageValue(key, storedValue);
  }, [key, storedValue]);

  const setValue = (value: T) => {
    setStoredValue(value);
  };

  const handleStorageChange = useCallback(() => {
    setStoredValue(getLocalStorageValue(key, initialValue));
  }, [key, initialValue]);

  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleStorageChange]);

  return [storedValue, setValue];
};
